import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Toast } from "primereact/toast";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import useUrlState from "@ahooksjs/use-url-state";
import MeaButton from "../../../../components/MeaButton";
import MeaDataTable from "../../../../components/MeaDataTable";
import MeaStudentAvatar from "../../components/MeaStudentAvatar";
import MeaLink from "../../../../components/MeaLink";
import { useAuthContext } from "../../../../hooks/useAuthContext";
import slotChangeRequestsAPI from "../../../../services/slot_change_requests";
import scheduleAPI from "../../../../services/schedule";
import classesAPI from "../../../../services/classes";
import {
  PERMISSION_TRAINING_REGISTRATION,
  PERMISSION_TRAINING_STUDENTS,
  PERMISSION_TRAINING_CLASSES,
  PERMISSION_TRAINING_SLOT_CHANGE_REQUESTS,
} from "../../../../permissions";
import CreateSlotChangeRequestModal from "./CreateSlotChangeRequestModal";
import ImportSlotChangeRequestsModal from "./ImportSlotChangeRequestsModal";
import UpdateSlotChangeRequestModal from "./UpdateSlotChangeRequestModal";
import RejectSlotChangeRequestModal from "./RejectSlotChangeRequestModal";
import SendSlotChangeRequestParentsMailModal from "./SendSlotChangeRequestParentsMailModal";
import DecisionDetailsSlotChangeRequestModal from "./DecisionDetailsSlotChangeRequestModal";
import SlotChangeProposalAlgorithmHelpDialog from "./SlotChangeProposalAlgorithmHelpDialog";
import {
  classTagStyle,
  formatRequestDate,
  formatSlotLabel,
  requestPreferences,
  slotsForClass,
  statusTag,
  targetClassesForPreference,
  urgencyTag,
} from "../slotChangeUtils";

const SlotChangeRequestsListComponent = forwardRef((props, ref) => {
  const { showAddButton = true, showFilterButton = true, showFilter = false } = props;
  const params = useParams();
  const schoolId = props.schoolId || params.schoolId;
  const trainingId = props.trainingId || params.trainingId;
  const accessToken = "";
  const navigate = useNavigate();
  const { hasPermission } = useAuthContext();

  const [page, setPage] = useState();
  const [pageSize, setPageSize] = useState();
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [helpVisible, setHelpVisible] = useState(false);
  const [state, setState] = useUrlState({
    requestStatus: "PENDING",
    requestUrgency: "all",
    requestStudentName: "",
    requestClassId: "",
  });

  const createModalRef = useRef();
  const importModalRef = useRef();
  const updateModalRef = useRef();
  const rejectModalRef = useRef();
  const sendParentsMailModalRef = useRef();
  const decisionDetailsModalRef = useRef();
  const searchInputRef = useRef();
  const toast = useRef();

  const queryList = slotChangeRequestsAPI.useGetList(
    schoolId,
    trainingId,
    accessToken,
    page,
    pageSize,
    {
      status: state.requestStatus,
      urgency: state.requestUrgency,
      student_name: state.requestStudentName,
      from_class_id: state.requestClassId,
    }
  );
  const classesQuery = classesAPI.useGetList(
    schoolId,
    trainingId,
    accessToken,
    1,
    1000,
    {}
  );
  const scheduleQuery = scheduleAPI.useGetList(
    schoolId,
    trainingId,
    accessToken,
    1,
    1000,
    {}
  );
  const classPath = (classId) =>
    "/school/" +
    schoolId +
    "/admin/trainings/" +
    trainingId +
    "/classes/" +
    classId;

  const liveSlotsForRequest = (item) =>
    slotsForClass({
      slots: [],
      scheduleItems: scheduleQuery?.data?.data,
      classId: item?.student?.class_id || item?.student?.classe?.id,
    });
  const classOptions = (classesQuery?.data?.data || [])
    .map((classe) => ({
      label: classe?.name || "",
      value: classe?.id || "",
    }))
    .filter((option) => option.label && option.value)
    .sort((left, right) => left.label.localeCompare(right.label, "fr", { sensitivity: "base" }));
  const requestRowClassName = (item) => {
    switch (item?.status) {
      case "APPROVED":
        return "bg-green-50";
      case "REJECTED":
        return "bg-red-50";
      case "DRIFTED":
        return "bg-yellow-50";
      case "CANCELLED":
        return "bg-gray-100";
      default:
        return "";
    }
  };

  useEffect(() => {
    if ((state.requestStudentName || "") !== searchInputValue) {
      setSearchInputValue(state.requestStudentName || "");
    }
  }, [state.requestStudentName]);

  useEffect(() => {
    if (!queryList?.isFetching) {
      setIsSearchLoading(false);
    }
  }, [queryList?.isFetching]);

  const handleSearch = () => {
    setIsSearchLoading(true);
    setState((s) => ({
      ...s,
      requestStudentName: searchInputValue,
    }));
  };

  const handleClearSearch = () => {
    if (state.requestStudentName !== "") {
      setIsSearchLoading(true);
    }
    setSearchInputValue("");
    setState((s) => ({
      ...s,
      requestStudentName: "",
    }));
  };

  const handleRefreshSnapshot = (item) => {
    const liveClassName = item?.student?.classe?.name || "Aucune classe";
    const liveSlots = liveSlotsForRequest(item);
    const liveSlotLabel = liveSlots.length
      ? liveSlots.map((slot) => formatSlotLabel(slot)).join(", ")
      : "Aucun créneau";

    confirmDialog({
      message: (
        <div className="flex flex-column gap-2">
          <span>
            Rafraîchir la classe et le créneau mémorisés sur cette demande à partir de
            l'affectation actuelle de l'élève ?
          </span>
          <small className="text-600">
            Nouvelle classe : <span className="font-medium text-900">{liveClassName}</span>
          </small>
          <small className="text-600">
            Nouveau créneau : <span className="font-medium text-900">{liveSlotLabel}</span>
          </small>
        </div>
      ),
      header: "Confirmation",
      icon: "pi pi-refresh",
      acceptLabel: "Rafraîchir",
      rejectLabel: "Annuler",
      accept: () => {
        slotChangeRequestsAPI
          .UpdateOne(schoolId, trainingId, accessToken, item.id, {
            refresh_snapshot: true,
          })
          .then(() => {
            toast.current.show({
              severity: "success",
              summary: "Succès",
              detail: "La demande a été rafraîchie avec la classe et le créneau actuels",
            });
            queryList.refetch();
          })
          .catch((err) =>
            toast.current.show({
              severity: "error",
              summary: "Erreur",
              detail: err.message,
            })
          );
      },
    });
  };

  useImperativeHandle(ref, () => ({
    refresh: () => queryList?.refetch(),
    createForStudent: (student) => {
      createModalRef.current.show(() => queryList.refetch(), student);
    },
  }));

  const startToolbarTemplate = (
    <div className="flex flex-wrap gap-2 align-items-center">
      <span className="p-input-icon-left">
        <IconField iconPosition="left">
          <InputIcon className="pi pi-search"> </InputIcon>
          <InputText
            id="slotChangeRequestStudentSearch"
            ref={searchInputRef}
            value={searchInputValue}
            onChange={(e) => setSearchInputValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            placeholder="Chercher un(e) élève"
          />
        </IconField>
      </span>
      <MeaButton
        onClick={handleSearch}
        icon={isSearchLoading ? "pi pi-spinner pi-spin" : "pi pi-search"}
        disabled={isSearchLoading}
        loading={isSearchLoading}
      />
      {searchInputValue && (
        <MeaButton
          onClick={handleClearSearch}
          icon="pi pi-times"
          outlined
          disabled={isSearchLoading}
        />
      )}
      {showAddButton && hasPermission(PERMISSION_TRAINING_SLOT_CHANGE_REQUESTS.CREATE) ? (
        <>
          <MeaButton
            label="Nouvelle demande"
            icon="pi pi-plus"
            size="small"
            className="p-button mr-2 action"
            onClick={() => {
              createModalRef.current.show(() => queryList.refetch());
            }}
          />
          <MeaButton
            label="Importer"
            icon="pi pi-upload"
            size="small"
            outlined
            className="p-button mr-2 action"
            onClick={() => {
              importModalRef.current.show();
            }}
          />
        </>
      ) : null}
      {hasPermission(PERMISSION_TRAINING_SLOT_CHANGE_REQUESTS.APPLY) ? (
        <>
          <MeaButton
            label="Proposer les affectations"
            icon="pi pi-sitemap"
            size="small"
            outlined
            className="p-button mr-2 action"
            onClick={() => {
              navigate(
                props.proposalsPath ||
                  "/school/" +
                    schoolId +
                    "/admin/slot-change-requests/" +
                    trainingId +
                    "/proposals"
              );
            }}
          />
          <MeaButton
            icon="pi pi-question-circle"
            size="small"
            severity="help"
            outlined
            rounded
            className="mr-2"
            tooltip="Expliquer l'algorithme de proposition"
            tooltipOptions={{ position: "top" }}
            aria-label="Expliquer l'algorithme de proposition"
            onClick={() => setHelpVisible(true)}
          />
        </>
      ) : null}
    </div>
  );

  return (
    <>
      <ConfirmDialog />
      <Toast ref={toast} position="bottom-center" />
      <SlotChangeProposalAlgorithmHelpDialog
        visible={helpVisible}
        onHide={() => setHelpVisible(false)}
      />
      <CreateSlotChangeRequestModal
        ref={createModalRef}
        schoolId={schoolId}
        trainingId={trainingId}
      />
      <ImportSlotChangeRequestsModal
        ref={importModalRef}
        schoolId={schoolId}
        trainingId={trainingId}
        onImported={() => queryList.refetch()}
      />
      <UpdateSlotChangeRequestModal
        ref={updateModalRef}
        request={selectedRequest}
        schoolId={schoolId}
        trainingId={trainingId}
      />
      <RejectSlotChangeRequestModal
        ref={rejectModalRef}
        schoolId={schoolId}
        trainingId={trainingId}
      />
      <SendSlotChangeRequestParentsMailModal
        ref={sendParentsMailModalRef}
        schoolId={schoolId}
        trainingId={trainingId}
        toast={toast}
        onSent={() => queryList.refetch()}
      />
      <DecisionDetailsSlotChangeRequestModal ref={decisionDetailsModalRef} />
      <MeaDataTable
        query={queryList}
        serviceAPI={slotChangeRequestsAPI}
        names={{
          pagination: "slotChangeRequest",
          singular: "demande",
          plural: "demandes",
        }}
        startToolbarTemplate={startToolbarTemplate}
        showFilterButton={showFilterButton}
        showFilter={showFilter}
        showDeleteButton={hasPermission(PERMISSION_TRAINING_SLOT_CHANGE_REQUESTS.DELETE)}
        setPage={setPage}
        setPageSize={setPageSize}
        schoolId={schoolId}
        trainingId={trainingId}
        accessToken={accessToken}
        rowClassName={requestRowClassName}
        customFilterState={state}
        excludeFilterFields={["requestStatus", "requestUrgency", "requestStudentName"]}
        paginatorPosition="bottom"
      >
        <Column selectionMode="multiple" headerStyle={{ width: "3rem" }} />
        <Column
          header="Élève"
          body={(item) => (
            <div className="flex flex-column gap-1">
              <MeaStudentAvatar
                student={item?.student}
                permission={PERMISSION_TRAINING_REGISTRATION.GET}
                schoolId={schoolId}
                trainingId={trainingId}
                link="candidates"
              />
              {item?.student?.status === "UNREGISTRED" ? (
                <small className="text-red-600 font-medium ml-5">Désinscrit</small>
              ) : null}
            </div>
          )}
        />
        <Column
          header="Classe actuelle"
          body={(item) => {
            const fromClass = item?.from_class;
            const fromSlots = item?.from_slot
              ? [item.from_slot]
              : slotsForClass({
                  slots: [],
                  scheduleItems: scheduleQuery?.data?.data,
                  classId: fromClass?.id || item?.from_class_id,
                });
            if (!fromClass && !fromSlots.length) {
              return "-";
            }
            return (
              <div className="flex flex-column">
                {fromClass ? (
                  <MeaLink
                    to={
                      "/school/" +
                      schoolId +
                      "/admin/trainings/" +
                      trainingId +
                      "/classes/" +
                      fromClass.id
                    }
                    permission={PERMISSION_TRAINING_CLASSES.GET}
                  >
                    {fromClass.name}
                  </MeaLink>
                ) : (
                  "-"
                )}
                {fromSlots.map((slot) => (
                  <small key={slot.id || slot.name} className="text-500">
                    {formatSlotLabel(slot)}
                  </small>
                ))}
              </div>
            );
          }}
          filter
          showFilterMenu={false}
          filterElement={() => (
            <Dropdown
              value={state.requestClassId || null}
              options={classOptions}
              optionLabel="label"
              optionValue="value"
              onChange={(e) =>
                setState((s) => ({
                  ...s,
                  requestClassId: e.value ?? "",
                }))
              }
              placeholder="Sélectionner"
              className="p-column-filter"
              style={{ minWidth: "12rem" }}
              showClear
            />
          )}
        />
        <Column
          header="Créneaux souhaités"
          body={(item) => {
            const prefs = requestPreferences(item);
            const approvedClassId = item?.approved_class_id || item?.approved_class?.id;
            if (!prefs.length) {
              return "-";
            }
            return (
              <div className="flex flex-column gap-1">
                {prefs.map((pref, index) => {
                  const slotId = pref.slot_id || pref.slot?.id;
                  const displayClasses = targetClassesForPreference(pref);
                  const isChosenPreference =
                    item?.status === "APPROVED" &&
                    approvedClassId &&
                    displayClasses.some((classe) => classe.id === approvedClassId);
                  return (
                    <div key={slotId || index}>
                      <div
                        className={`flex align-items-center gap-1 ${
                          isChosenPreference ? "font-semibold text-900" : ""
                        }`}
                      >
                        {isChosenPreference ? (
                          <i
                           className="pi pi-check-circle text-green-600"
                           style={{ fontSize: "0.75rem" }}
                          />
                        ) : null}
                        {index + 1}. {formatSlotLabel(pref.slot)}
                      </div>
                      <small className="flex flex-wrap gap-1 mt-1">
                        {displayClasses.length
                          ? displayClasses.map((classe) => (
                             <span
                               key={classe.id}
                               className={`inline-flex align-items-center gap-1 ${
                                 item?.status === "APPROVED" &&
                                 approvedClassId === classe.id
                                   ? "font-semibold"
                                   : ""
                               }`}
                             >
                               {item?.status === "APPROVED" &&
                               approvedClassId === classe.id ? (
                                 <i
                                   className="pi pi-check-circle text-green-600"
                                   style={{ fontSize: "0.75rem" }}
                                 />
                               ) : null}
                               <MeaLink
                                 to={classPath(classe.id)}
                                 permission={PERMISSION_TRAINING_CLASSES.GET}
                               >
                                 <Tag
                                   value={classe.name}
                                   style={classTagStyle(classe)}
                                   className={`text-xs ${
                                     item?.status === "APPROVED" &&
                                     approvedClassId === classe.id
                                       ? "font-semibold"
                                       : ""
                                   }`}
                                 />
                               </MeaLink>
                             </span>
                           ))
                          : (
                           <span className="text-500">Aucune classe cible</span>
                          )}
                      </small>
                    </div>
                  );
                })}
              </div>
            );
          }}
        />
        <Column
          header="Date et heure"
          body={(item) => formatRequestDate(item?.requested_at)}
        />
        <Column
          header="Urgence"
          body={(item) => {
            const tag = urgencyTag(item?.urgency);
            return <Tag value={tag.value} severity={tag.severity} icon={tag.icon} style={tag.style} />;
          }}
          filter
          showFilterMenu={false}
          filterElement={() => (
            <Dropdown
              value={state.requestUrgency}
              options={[
                { label: "Toutes", value: "all" },
                { label: "Neutre", value: "NORMAL" },
                { label: "Prioritaire", value: "HIGH" },
                { label: "Urgent", value: "URGENT" },
              ]}
              onChange={(e) =>
                setState((s) => ({ ...s, requestUrgency: e.value ?? "all" }))
              }
              className="p-column-filter"
              style={{ minWidth: "8rem" }}
            />
          )}
        />
        <Column
          header="Statut"
          body={(item) => {
            const tag = statusTag(item?.status);
            return <Tag value={tag.value} severity={tag.severity} />;
          }}
          filter
          showFilterMenu={false}
          filterElement={() => (
            <Dropdown
              value={state.requestStatus}
              options={[
                { label: "Tous", value: "all" },
                { label: "En attente", value: "PENDING" },
                { label: "Dérivées", value: "DRIFTED" },
                { label: "Approuvées", value: "APPROVED" },
                { label: "Refusées", value: "REJECTED" },
              ]}
              onChange={(e) =>
                setState((s) => ({ ...s, requestStatus: e.value ?? "all" }))
              }
              className="p-column-filter"
              style={{ minWidth: "8rem" }}
            />
          )}
        />
        <Column
          header="Motif"
          body={(item) => {
            const requestReason = item?.reason;
            const rejectionReason = item?.rejection_reason;
            if (item?.status === "REJECTED") {
              if (!rejectionReason && !requestReason) {
                return "-";
              }
              return (
                <div className="flex flex-column">
                  {rejectionReason ? (
                    <span>{rejectionReason}</span>
                  ) : (
                    <span className="text-500">Aucun motif de refus</span>
                  )}
                  {requestReason ? (
                    <small className="text-500">Demande : {requestReason}</small>
                  ) : null}
                </div>
              );
            }
            return requestReason || "-";
          }}
          style={{ maxWidth: "260px" }}
        />
        <Column
          body={(item) =>
            item?.status === "PENDING" || item?.status === "DRIFTED" ? (
              <div className="flex justify-content-center gap-1">
                <MeaButton
                  icon="pi pi-info-circle"
                  rounded
                  text
                  severity="secondary"
                  style={{ padding: "0", height: "1.5rem" }}
                  tooltip="Voir les détails de la demande"
                  tooltipOptions={{ position: "top" }}
                  aria-label="Voir les détails de la demande"
                  onClick={() => decisionDetailsModalRef.current.show(item)}
                />
                {item?.status === "DRIFTED" && hasPermission(PERMISSION_TRAINING_SLOT_CHANGE_REQUESTS.UPDATE) ? (
                  <MeaButton
                    icon="pi pi-refresh"
                    rounded
                    text
                    severity="warning"
                    style={{ padding: "0", height: "1.5rem" }}
                    tooltip="Rafraîchir la classe et le créneau mémorisés"
                    tooltipOptions={{ position: "top" }}
                    aria-label="Rafraîchir la demande"
                    onClick={() => handleRefreshSnapshot(item)}
                  />
                ) : null}
                {hasPermission(PERMISSION_TRAINING_SLOT_CHANGE_REQUESTS.UPDATE) ? (
                  <MeaButton
                    icon="pi pi-pencil"
                    rounded
                    text
                    style={{ padding: "0", height: "1.5rem" }}
                    tooltip="Modifier la demande"
                    tooltipOptions={{ position: "top" }}
                    aria-label="Modifier la demande"
                    onClick={() => {
                      setSelectedRequest(item);
                      updateModalRef.current.show(() => queryList.refetch(), item);
                    }}
                  />
                ) : null}
                {hasPermission(PERMISSION_TRAINING_SLOT_CHANGE_REQUESTS.APPLY) ? (
                  <MeaButton
                    icon="pi pi-times"
                    rounded
                    text
                    severity="danger"
                    style={{ padding: "0", height: "1.5rem" }}
                    tooltip="Refuser la demande"
                    tooltipOptions={{ position: "top" }}
                    aria-label="Refuser la demande"
                    onClick={() =>
                      rejectModalRef.current.show(item, () => queryList.refetch())
                    }
                  />
                ) : null}
                {hasPermission(PERMISSION_TRAINING_SLOT_CHANGE_REQUESTS.DELETE) ? (
                  <MeaButton
                    icon="pi pi-trash"
                    rounded
                    text
                    severity="danger"
                    style={{ padding: "0", height: "1.5rem" }}
                    tooltip="Supprimer définitivement la demande"
                    tooltipOptions={{ position: "top" }}
                    aria-label="Supprimer la demande"
                    onClick={() => {
                      confirmDialog({
                        message: "Supprimer définitivement cette demande ?",
                        header: "Confirmation",
                        icon: "pi pi-info-circle",
                        acceptLabel: "Oui",
                        rejectLabel: "Non",
                        accept: () => {
                          slotChangeRequestsAPI
                            .DeleteOne(schoolId, trainingId, accessToken, item.id)
                            .then(() => queryList.refetch())
                            .catch((err) =>
                              toast.current.show({
                                severity: "error",
                                summary: "Erreur",
                                detail: err.message,
                              })
                            );
                        },
                      });
                    }}
                  />
                ) : null}
              </div>
            ) : item?.status === "APPROVED" || item?.status === "REJECTED" ? (
              <div className="flex justify-content-center gap-1">
                <MeaButton
                  icon="pi pi-info-circle"
                  rounded
                  text
                  severity="secondary"
                  style={{ padding: "0", height: "1.5rem" }}
                  tooltip="Voir les détails de la demande"
                  tooltipOptions={{ position: "top" }}
                  aria-label="Voir les détails de la demande"
                  onClick={() => decisionDetailsModalRef.current.show(item)}
                />
                {item?.parents_decision_email_sent_at ? (
                  <span
                    className="inline-flex align-items-center gap-1 text-green-600 text-xs font-medium"
                    title="Les parents ont déjà été informés de cette décision par e-mail"
                  >
                    <i className="pi pi-check-circle" />
                    Informés
                  </span>
                ) : (
                  <>
                    {item?.status === "REJECTED" && hasPermission(PERMISSION_TRAINING_SLOT_CHANGE_REQUESTS.APPLY) ? (
                      <MeaButton
                        icon="pi pi-pencil"
                        rounded
                        text
                        severity="warning"
                        style={{ padding: "0", height: "1.5rem" }}
                        tooltip="Modifier le motif de refus"
                        tooltipOptions={{ position: "top" }}
                        aria-label="Modifier le motif de refus"
                        onClick={() =>
                          rejectModalRef.current.show(
                            item,
                            () => queryList.refetch(),
                            item?.rejection_reason || "",
                            {
                              title: "Modifier le motif de refus",
                              submitLabel: "Enregistrer",
                              successMessage: "Motif de refus mis à jour",
                            }
                          )
                        }
                      />
                    ) : null}
                    {hasPermission(PERMISSION_TRAINING_SLOT_CHANGE_REQUESTS.APPLY) ? (
                      <MeaButton
                        icon="pi pi-envelope"
                        rounded
                        text
                        severity="info"
                        style={{ padding: "0", height: "1.5rem" }}
                        tooltip="Envoyer un mail aux parents"
                        tooltipOptions={{ position: "top" }}
                        aria-label="Envoyer un mail aux parents"
                        onClick={() => sendParentsMailModalRef.current.show(item.id)}
                      />
                    ) : null}
                  </>
                )}
              </div>
            ) : null
          }
          headerStyle={{ width: "8rem" }}
          bodyStyle={{ textAlign: "center" }}
        />
      </MeaDataTable>
    </>
  );
});

export default SlotChangeRequestsListComponent;
